import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  Text,
  Button,
  Select,
  TextInput,
  Box,
  Group,
  NumberInput,
  Accordion,
  Badge,
  Grid,
  Stack,
  RangeSlider,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import LoadingData from '@components/graphql-loading-data';
import GraphqlError from '@components/graphql-error';
import { getSelectDataFromEnum, alphabeticalSortOther } from '@lib/enum-utils';
import {
  TripProfileStatus,
  TravelPartyType,
  AccommodationType,
  OccasionType,
  ScheduleDensity,
  CulturalComfortLevel,
  SupportLevel,
} from '@generated/graphql';
import { UPSERT_TRIP_PROFILE_DETAILS, GET_TRIP_PROFILES, GET_TRIP_PROFILE_DETAILS } from '@queries/trip-profiles';
import classes from './trip-profile-form.module.css';

function TripProfileForm() {
  const { clientId, tripProfileId } = useParams();
  const navigate = useNavigate();

  const {
    data,
    loading: queryLoading,
    error: queryError,
  } = useQuery(GET_TRIP_PROFILE_DETAILS, {
    variables: { id: tripProfileId || '' }, // TODO this need fixed, should not be empty string
    skip: !tripProfileId,
  });

  const form = useForm({
    initialValues: {
      title: '',
      status: '',
      startDate: null as unknown as Date,
      endDate: null as unknown as Date,
      travelPartyType: null as unknown as TravelPartyType,
      numberTravelers: null as unknown as number,
      budgetLower: null as unknown as number,
      budgetUpper: null as unknown as number,
      accommodationType: null as unknown as AccommodationType,
      occasionType: null as unknown as OccasionType,
      scheduleDensity: null as unknown as ScheduleDensity,
      culturalComfortLevel: null as unknown as CulturalComfortLevel,
      supportLevel: null as unknown as SupportLevel,
    },
    validate: {
      title: (value) => (value.trim().length < 5 ? 'Please enter a title' : null),
      status: (value) => (value.trim().length < 1 ? 'Please select a status' : null),
      startDate: (value) => (value === null ? 'Please enter a valid date' : null),
      endDate: (value, values) => (value <= values.startDate ? 'Please enter date after the start date' : null),
      numberTravelers: (value) => (value === null || value > 0 ? null : 'Please enter one or greater'),
      budgetLower: (value) => (value === null || value >= 1 ? null : 'Please enter 1 or greater dollar amount'),
      budgetUpper: (value, values) =>
        value === null || (value > 1 && values.budgetLower !== null && value > values.budgetLower)
          ? null
          : `Must be greater then lower budget amount: ${values.budgetLower}`,
    },
  });

  useEffect(() => {
    if (data) {
      form.setValues({
        title: data.singleTripProfile.title,
        status: data.singleTripProfile.status || '',
        startDate: data.singleTripProfile.startDate ? new Date(data.singleTripProfile.startDate) : undefined,
        endDate: data.singleTripProfile.endDate ? new Date(data.singleTripProfile.endDate) : undefined,
        travelPartyType: data.singleTripProfile.travelPartyType ?? undefined,
        numberTravelers: data.singleTripProfile.numberTravelers ?? undefined,
        budgetLower: data.singleTripProfile.budgetLower ?? undefined,
        budgetUpper: data.singleTripProfile.budgetUpper ?? undefined,
        accommodationType: data.singleTripProfile.accommodationType ?? undefined,
        occasionType: data.singleTripProfile.occasionType ?? undefined,
        scheduleDensity: data.singleTripProfile.scheduleDensity ?? undefined,
        culturalComfortLevel: data.singleTripProfile.culturalComfortLevel ?? undefined,
        supportLevel: data.singleTripProfile.supportLevel ?? undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const [upsertClientTripProfile, { loading, error }] = useMutation(UPSERT_TRIP_PROFILE_DETAILS, {
    refetchQueries: [{ query: GET_TRIP_PROFILES, variables: { clientId } }], // TODO: ?? GET_TRIP_PROFILE_DETAILS
    awaitRefetchQueries: true,
    errorPolicy: 'all',
  });

  if (queryLoading) return <LoadingData />;
  if (queryError) return <GraphqlError error={queryError} />;
  if (loading) return <LoadingData />;
  if (error) return <GraphqlError error={error} />;

  type FormValues = {
    title: string;
    status: string;
    startDate: Date;
    endDate: Date;
    travelPartyType: string;
    numberTravelers: number;
    budgetLower: number;
    budgetUpper: number;
    accommodationType: string;
    occasionType: string;
    scheduleDensity: string;
    culturalComfortLevel: string;
    supportLevel: string;
  };

  const saveTrip = async (values: FormValues) => {
    const result = await upsertClientTripProfile({
      variables: {
        id: tripProfileId || null,
        clientId: clientId || '', // TODO clientId should  never be empty string or null!
        title: values.title,
        status: values.status as TripProfileStatus,
        startDate: new Date(values.startDate).toISOString(),
        endDate: new Date(values.endDate).toISOString(),
        travelPartyType: values.travelPartyType as TravelPartyType,
        numberTravelers: values.numberTravelers,
        budgetLower: values.budgetLower,
        budgetUpper: values.budgetUpper,
        accommodationType: values.accommodationType as AccommodationType,
        occasionType: values.occasionType as OccasionType,
        scheduleDensity: values.scheduleDensity as ScheduleDensity,
        culturalComfortLevel: values.culturalComfortLevel as CulturalComfortLevel,
        supportLevel: values.supportLevel as SupportLevel,
      },
    });

    if (result.errors) {
      Sentry.captureMessage(`Error(s) saving TravelProfile [id: ${tripProfileId}]`, {
        level: 'error',
        extra: { errors: result.errors },
      });
      return;
    }

    const savedId = result.data?.upsertClientTripProfile?.id;
    if (!tripProfileId && savedId) {
      // New trip — go to survey page, which will open the destination choice modal
      navigate(`/clients/${clientId}/trip-profiles/${savedId}`);
    } else {
      navigate(`/clients/${clientId}`);
    }
  };

  const cancel = () => {
    navigate(-1);
  };

  // Helper: compute completion badge for optional section
  const optionalKeys = [
    'travelPartyType',
    'numberTravelers',
    'budgetLower',
    'budgetUpper',
    'accommodationType',
    'occasionType',
    'scheduleDensity',
    'culturalComfortLevel',
    'supportLevel',
  ] as const;

  const completedCount = optionalKeys.filter((k) => {
    const v = (form.values as Record<typeof k, unknown>)[k];
    return v !== null && v !== undefined && v !== '';
  }).length;

  const optionalTotal = optionalKeys.length;

  // Budget helpers
  const budgetLower =
    typeof form.values.budgetLower === 'number' && !isNaN(form.values.budgetLower) ? form.values.budgetLower : 1;
  const budgetUpper =
    typeof form.values.budgetUpper === 'number' && !isNaN(form.values.budgetUpper)
      ? form.values.budgetUpper
      : Math.max(budgetLower, 5000);
  const budgetRange: [number, number] = [budgetLower, budgetUpper];
  const fmtUSD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  return (
    <Box maw={500} mx="auto">
      <form onSubmit={form.onSubmit(saveTrip)}>
        <TextInput label="Trip Profile Name" placeholder="Name of Trip" {...form.getInputProps('title')} />
        <DateInput
          label="Activity Start Date"
          placeholder="Pick first day for activities"
          {...form.getInputProps('startDate')}
        />
        <DateInput
          label="Activity End Date"
          placeholder="Pick last day for activities"
          {...form.getInputProps('endDate')}
        />
        <Select
          mt="md"
          data={getSelectDataFromEnum(TripProfileStatus, {
            manualOrder: ['Planning', 'Pending', 'Outbound', 'Archived'],
          })}
          placeholder="Pick one"
          label="Trip Profile Status"
          {...form.getInputProps('status')}
        />
        {/* Replace the current Optional Intake box with the accordion below */}
        <Accordion mt="md" className={classes.box} defaultValue="optional">
          <Accordion.Item value="optional">
            <Accordion.Control>
              <Group gap="xs" align="center">
                <Text fw={600}>Optional Reference Only</Text>
                <Badge variant="light" size="sm">
                  {completedCount}/{optionalTotal} filled
                </Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Grid mt="sm">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Stack gap="xs">
                    <Select
                      label="Who is going?"
                      data={getSelectDataFromEnum(TravelPartyType, {
                        manualOrder: ['Solo', 'Couple', 'Family', 'Friends', 'Group'],
                      })}
                      placeholder="Pick one"
                      {...form.getInputProps('travelPartyType')}
                    />{' '}
                  </Stack>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <NumberInput
                    label="Number of travelers"
                    min={1}
                    step={1}
                    placeholder="1"
                    {...form.getInputProps('numberTravelers')}
                  />
                </Grid.Col>

                <Grid.Col span={12}>
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>
                      Budget range
                    </Text>
                    <RangeSlider
                      min={1}
                      max={50000}
                      step={500}
                      value={budgetRange}
                      onChange={([low, high]) => {
                        form.setFieldValue('budgetLower', typeof low === 'number' && !isNaN(low) ? low : 1);
                        form.setFieldValue(
                          'budgetUpper',
                          typeof high === 'number' && !isNaN(high) ? high : Math.max(budgetLower, 5000)
                        );
                      }}
                      label={(val) => fmtUSD.format(val)}
                      marks={[
                        { value: 1, label: '$1' },
                        { value: 10000, label: '$10k' },
                        { value: 25000, label: '$25k' },
                        { value: 50000, label: '$50k' },
                      ]}
                    />
                    <Group grow>
                      <NumberInput
                        label="Lower"
                        min={1}
                        step={100}
                        prefix="$ "
                        thousandSeparator=","
                        {...form.getInputProps('budgetLower')}
                      />
                      <NumberInput
                        label="Upper"
                        min={1}
                        step={100}
                        prefix="$ "
                        thousandSeparator=","
                        {...form.getInputProps('budgetUpper')}
                      />
                    </Group>
                  </Stack>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select
                    label="Preferred accommodation type"
                    data={getSelectDataFromEnum(AccommodationType, { sortFn: alphabeticalSortOther })}
                    placeholder="Pick one"
                    {...form.getInputProps('accommodationType')}
                  />
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select
                    label="Special occasion"
                    data={getSelectDataFromEnum(OccasionType, { sortFn: alphabeticalSortOther })}
                    placeholder="Pick one"
                    {...form.getInputProps('occasionType')}
                  />
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select
                    label="Schedule density"
                    data={getSelectDataFromEnum(ScheduleDensity, { sortFn: alphabeticalSortOther })}
                    placeholder="Pick one"
                    {...form.getInputProps('scheduleDensity')}
                  />
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select
                    label="Cultural comfort level"
                    data={getSelectDataFromEnum(CulturalComfortLevel, { sortFn: alphabeticalSortOther })}
                    placeholder="Pick one"
                    {...form.getInputProps('culturalComfortLevel')}
                  />
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select
                    label="Support level required"
                    data={getSelectDataFromEnum(SupportLevel, { sortFn: alphabeticalSortOther })}
                    placeholder="Pick one"
                    {...form.getInputProps('supportLevel')}
                  />
                </Grid.Col>
              </Grid>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
        <Group mt="md">
          <Button type="submit">Save</Button>
          <Button onClick={cancel}>Cancel</Button>
        </Group>
      </form>
    </Box>
  );
}

export default TripProfileForm;
